/**
 * 回転した地図 (bearing) の画面の向きで点群の範囲を測る。
 *
 * 「地図を合わせる」は北が上の前提で経緯度の範囲から center/zoom を出していたため、
 * 地図を回すと範囲が合わない (はみ出す・余る)。点を Web Mercator の世界座標
 * (0〜1、x=東、y=南) に移し、画面の向き (bearing) に回してから範囲を測る。
 *
 * 画面の向き: bearing θ は「真上が北から時計回りに θ」の向き (Mapbox と同じ)。
 *   画面 = (cosθ·x + sinθ·y, −sinθ·x + cosθ·y)  (x=東, y=南, 画面 y=下)
 */
import type { LngLat } from '@/types/property';

/** 経緯度 → Web Mercator の世界座標 (0〜1)。 */
export function toWorld(p: LngLat): [number, number] {
  const x = (p.lng + 180) / 360;
  const s = Math.sin((p.lat * Math.PI) / 180);
  const y = 0.5 - Math.log((1 + s) / (1 - s)) / (4 * Math.PI);
  return [x, y];
}

/** 世界座標 → 経緯度。 */
export function fromWorld([x, y]: [number, number]): LngLat {
  const lng = x * 360 - 180;
  const lat = (Math.atan(Math.exp((0.5 - y) * 2 * Math.PI)) * 360) / Math.PI - 90;
  return { lng, lat };
}

function rad(bearing: number): number {
  return (bearing * Math.PI) / 180;
}

/** 世界座標を画面の向きへ回す。 */
export function toScreenFrame([x, y]: [number, number], bearing: number): [number, number] {
  const t = rad(bearing);
  return [Math.cos(t) * x + Math.sin(t) * y, -Math.sin(t) * x + Math.cos(t) * y];
}

/** 画面の向きから世界座標へ戻す。 */
export function fromScreenFrame([sx, sy]: [number, number], bearing: number): [number, number] {
  const t = rad(bearing);
  return [Math.cos(t) * sx - Math.sin(t) * sy, Math.sin(t) * sx + Math.cos(t) * sy];
}

export interface ScreenExtent {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
}

/** 点群の、画面の向きでの範囲 (世界座標の単位)。 */
export function screenExtent(points: LngLat[], bearing: number): ScreenExtent {
  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  for (const p of points) {
    const [sx, sy] = toScreenFrame(toWorld(p), bearing);
    minX = Math.min(minX, sx);
    maxX = Math.max(maxX, sx);
    minY = Math.min(minY, sy);
    maxY = Math.max(maxY, sy);
  }
  return { minX, maxX, minY, maxY };
}

/** 画面の向きでの点 (世界座標の単位) → 経緯度。 */
export function screenPointToLngLat(sx: number, sy: number, bearing: number): LngLat {
  return fromWorld(fromScreenFrame([sx, sy], bearing));
}

/** 角度を 0〜359 の整数にそろえる (表示・保存用)。 */
export function normalizeBearing(b: number | undefined): number {
  const r = Math.round(b ?? 0) % 360;
  return r < 0 ? r + 360 : r;
}
